// DebugPanel.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { format, fromUnixTime } from 'date-fns';

import DrmPanelTestSection from '../component/DrmPanelTestSection.jsx';
import Loading from '../component/Loading.jsx';
import Accordion, { Toggle, Collapse } from '../component/Accordion.jsx';
import { drmRulesetTestStatus } from '../resource/drmConstants.js';
import { color } from '../style/variables.js';
import { getUserAgent } from '../resource/getUserAgent.js';
import {
  CONFIG_PRIORITY_CONFIGURE,
  CONFIG_PRIORITY_PRESENCE_CLIENT,
  CONFIG_PRIORITY_PRESENCE_USER,
} from '../resource/configPriority.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import DropDownIcon from '../../img/ic-dropdown-white-16-x-16.svg';

const {
  ua,
  browser: uaBrowser,
  os: uaOs,
  device: uaDevice,
  engine: uaEngine,
} = getUserAgent();

import {
  StyledModal,
  Header,
  BackButton,
  ActionButton,
  Title,
  Body as SettingModalBody,
  Section,
  SectionBody,
  SectionTitle,
  SectionRow,
  Row,
  RowTitle,
  RowContent,
  TestedAt,
  AccordionRow,
  AccordionSectionRow,
  AccordionRowTitle,
  AccordionDropDownIcon,
  AccordionRowContent,
} from '../style/SettingModal.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import BackIconSource from '../../img/ic_topbar_back.svg';

export class DebugPanel extends React.PureComponent {
  state = {
    shouldShowExtraInformation: false,
    remoteConfigAccordion: {},
  };
  titleClickTimes = 0;
  trackUserClick = ({ buttonId }) => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({
          pathname: window.location.pathname,
        }),
        buttonId,
      },
    });
  };

  componentDidUpdate(prevProps) {
    const { isSpeedtested, drmTestStatus, trackSpeedtestEvent } = this.props;
    if (
      isSpeedtested !== prevProps.isSpeedtested ||
      drmTestStatus !== prevProps.drmTestStatus
    ) {
      if (isSpeedtested && drmTestStatus === drmRulesetTestStatus.TESTED) {
        trackSpeedtestEvent();
      }
    }
  }
  handleSpeedtestButtonOnClick = () => {
    const { checkNetworkSpeed, recheckDrmCapability } = this.props;
    this.trackUserClick({ buttonId: ButtonId.All.ButtonCheck });
    checkNetworkSpeed();
    recheckDrmCapability();
  };
  renderSpeedtestTimestamp = () => {
    const { speedtestUnixTimestamp } = this.props;
    return speedtestUnixTimestamp ? (
      <TestedAt>
        {format(fromUnixTime(speedtestUnixTimestamp), 'yyyy/MM/dd HH:mm:ss')}
      </TestedAt>
    ) : null;
  };
  renderSpeedtestPanel = () => {
    const {
      jitterStatus,
      pingStatus,
      dlProgress,
      ulProgress,
      dlStatus,
      ulStatus,
      isSpeedtesting,
    } = this.props;
    return (
      <>
        <Section key={'Speedtest'}>
          <SectionTitle>
            <div>Speedtest</div>
            {this.renderSpeedtestTimestamp()}
          </SectionTitle>
          <SectionBody>
            <Row>
              <SectionRow>
                <RowTitle>Ping</RowTitle>
                <RowContent>
                  {isSpeedtesting && !pingStatus ? (
                    <Loading />
                  ) : pingStatus ? (
                    `${pingStatus} ms`
                  ) : null}
                </RowContent>
              </SectionRow>
            </Row>
            <Row>
              <SectionRow>
                <RowTitle>Jitter</RowTitle>
                <RowContent>
                  {isSpeedtesting && !jitterStatus ? (
                    <Loading />
                  ) : jitterStatus ? (
                    `${jitterStatus} ms`
                  ) : null}
                </RowContent>
              </SectionRow>
            </Row>
            <Row>
              <SectionRow>
                <RowTitle>
                  Download
                  {dlProgress
                    ? ` (${Number.parseInt(dlProgress * 100)}%)`
                    : null}
                  {dlProgress > 0 && dlProgress < 1 ? (
                    <Loading
                      style={{ display: 'inline-block', marginLeft: '8px' }}
                    />
                  ) : null}
                </RowTitle>
                <RowContent>
                  {isSpeedtesting && !dlStatus ? (
                    <Loading />
                  ) : dlStatus ? (
                    `${dlStatus} Mbit/s`
                  ) : null}
                </RowContent>
              </SectionRow>
            </Row>
            <Row>
              <SectionRow>
                <RowTitle>
                  Upload
                  {ulProgress
                    ? ` (${Number.parseInt(ulProgress * 100)}%)`
                    : null}
                  {ulProgress > 0 && ulProgress < 1 ? (
                    <Loading
                      style={{ display: 'inline-block', marginLeft: '8px' }}
                    />
                  ) : null}
                </RowTitle>
                <RowContent>
                  {isSpeedtesting && !ulStatus ? (
                    <Loading />
                  ) : ulStatus ? (
                    `${ulStatus} Mbit/s`
                  ) : null}
                </RowContent>
              </SectionRow>
            </Row>
          </SectionBody>
        </Section>
      </>
    );
  };

  renderDrmPanel = () => {
    const { drmTestAtUnixTimestamp, messageId } = this.props;
    return (
      <DrmPanelTestSection
        drmTestAtUnixTimestamp={drmTestAtUnixTimestamp}
        messageId={messageId}
      />
    );
  };
  renderPusherPanel = () => {
    const { pusherStatsInitSpeed, pusherStatsConnectSpeed } = this.props;
    return (
      <Section key={'Pusher'}>
        <SectionTitle>Pusher Stats</SectionTitle>
        <SectionBody>
          <Row>
            <SectionRow>
              <RowTitle>Init Speed</RowTitle>
              <RowContent>{pusherStatsInitSpeed}</RowContent>
            </SectionRow>
          </Row>
          <Row>
            <SectionRow>
              <RowTitle>Connect Speed</RowTitle>
              <RowContent>{pusherStatsConnectSpeed}</RowContent>
            </SectionRow>
          </Row>
        </SectionBody>
      </Section>
    );
  };
  renderIdInformation = () => {
    const { clientId, sessionId } = this.props;
    return (
      <Section key={'ID Information'}>
        <SectionTitle>ID Information</SectionTitle>
        <SectionBody>
          <Row>
            <SectionRow>
              <RowTitle>Client ID</RowTitle>
              <RowContent>{clientId}</RowContent>
            </SectionRow>
          </Row>
          <Row>
            <SectionRow>
              <RowTitle>Session ID</RowTitle>
              <RowContent>{sessionId}</RowContent>
            </SectionRow>
          </Row>
        </SectionBody>
      </Section>
    );
  };
  renderNtp = () => {
    const { clientTimestamp, serverTimestamp } = this.props;
    const offset =
      clientTimestamp == null || serverTimestamp == null
        ? null
        : Math.floor((serverTimestamp - clientTimestamp) / 1000);
    return (
      <Section key={'NTP'}>
        <SectionTitle>{`NTP ${
          offset == null ? '' : `(Offset: ${offset}s)`
        }`}</SectionTitle>
        <SectionBody>
          <Row>
            <SectionRow>
              <RowTitle>Client</RowTitle>
              <RowContent>{clientTimestamp}</RowContent>
            </SectionRow>
          </Row>
          <Row>
            <SectionRow>
              <RowTitle>Server</RowTitle>
              <RowContent>{serverTimestamp}</RowContent>
            </SectionRow>
          </Row>
        </SectionBody>
      </Section>
    );
  };
  renderUserAgentInformation = () => {
    const renderItem = target =>
      Object.keys(target).map(key => (
        <Row key={key}>
          <SectionRow>
            <RowTitle>{key}</RowTitle>
            <RowContent>{target[key]}</RowContent>
          </SectionRow>
        </Row>
      ));
    return (
      <>
        {uaBrowser && (
          <Section>
            <SectionTitle>Browser</SectionTitle>
            <SectionBody>{renderItem(uaBrowser)}</SectionBody>
          </Section>
        )}
        {uaDevice && (
          <Section>
            <SectionTitle>Device</SectionTitle>
            <SectionBody>{renderItem(uaDevice)}</SectionBody>
          </Section>
        )}
        {uaOs && (
          <Section>
            <SectionTitle>OS</SectionTitle>
            <SectionBody>{renderItem(uaOs)}</SectionBody>
          </Section>
        )}
        {uaEngine && (
          <Section>
            <SectionTitle>Engine</SectionTitle>
            <SectionBody>{renderItem(uaEngine)}</SectionBody>
          </Section>
        )}
      </>
    );
  };
  getAbPriorityName = ({ priority }) => {
    switch (priority) {
      case CONFIG_PRIORITY_CONFIGURE:
        return 'configure.js';
      case CONFIG_PRIORITY_PRESENCE_CLIENT:
        return 'client';
      case CONFIG_PRIORITY_PRESENCE_USER:
        return 'user';
      default:
        return '';
    }
  };
  renderAbPanel = () => {
    const { abData } = this.props;
    const { shouldShowExtraInformation } = this.state;
    if (!shouldShowExtraInformation) {
      return null;
    }
    return Object.keys(abData).map(priority => {
      if (!abData[priority]) {
        return null;
      }
      const experimentObject = abData[priority].experiments;

      if (!experimentObject) return null;

      return Object.keys(experimentObject).map(experimentName => {
        const variableObject = experimentObject[experimentName].variables || {};
        const variation = experimentObject[experimentName].variation;
        return (
          <Section key={`${priority}:${experimentName}`}>
            <SectionTitle>
              AB:{this.getAbPriorityName({ priority })}:{experimentName}
            </SectionTitle>
            <SectionBody>
              <Row>
                <SectionRow>
                  <RowTitle>Variation</RowTitle>
                  <RowContent>{variation}</RowContent>
                </SectionRow>
              </Row>
              {!variableObject
                ? null
                : Object.keys(variableObject).map(variableName => (
                    <Row key={variableName}>
                      <SectionRow>
                        <RowTitle>{variableName}</RowTitle>
                        <RowContent>
                          {`${variableObject[variableName]}`}
                        </RowContent>
                      </SectionRow>
                    </Row>
                  ))}
            </SectionBody>
          </Section>
        );
      });
    });
  };

  renderFcmTokenPanel = () => {
    const { notificationPermission, fcmToken } = this.props;

    const { shouldShowExtraInformation } = this.state;
    if (!shouldShowExtraInformation) {
      return null;
    }

    return (
      <Section key={'FcmToken'}>
        <SectionTitle>FCM</SectionTitle>
        <SectionBody>
          <Row>
            <SectionRow>
              <RowTitle>Permission</RowTitle>
              <RowContent>{notificationPermission}</RowContent>
            </SectionRow>
          </Row>
          <TokenRow>
            <SectionRow>
              <TokenRowTitle>Token</TokenRowTitle>
              <TokenRowContent>{fcmToken}</TokenRowContent>
            </SectionRow>
          </TokenRow>
        </SectionBody>
      </Section>
    );
  };
  renderConfigurationGroups = () => {
    const {
      configurationGroups,
      remoteConfigMerged,
      currentConfigIds,
      currentConfigKey,
    } = this.props;
    const {
      shouldShowExtraInformation,
      remoteConfigAccordion: stateRemoteConfigAccordion,
    } = this.state;
    if (!shouldShowExtraInformation || !configurationGroups) {
      return null;
    }
    const currentConfigIdsAccordionId = 'current-config-ids';
    const isCurrentConfigIdsActive =
      !!stateRemoteConfigAccordion[currentConfigIdsAccordionId];
    return (
      <Section>
        <SectionTitle>Configuration Groups</SectionTitle>
        <SectionBody>
          {Object.entries(configurationGroups).map(([priority, value]) => {
            const text = JSON.stringify(value, null, 2);
            const accordionId = `configuration-groups-${priority}`;
            const isActive = !!stateRemoteConfigAccordion[accordionId];
            return (
              <AccordionRow key={priority}>
                <Accordion
                  activeKey={isActive ? accordionId : null}
                  key={accordionId}
                  as={AccordionSectionRow}
                >
                  <Toggle
                    eventKey={accordionId}
                    as={AccordionRowTitle}
                    isActive={isActive}
                    onClick={() => {
                      this.setState({
                        remoteConfigAccordion: {
                          ...stateRemoteConfigAccordion,
                          [accordionId]: !isActive,
                        },
                      });
                    }}
                  >
                    {priority} (Merged: {`${remoteConfigMerged?.[priority]}`})
                    <AccordionDropDownIcon
                      src={DropDownIcon}
                      isActive={isActive}
                    />
                  </Toggle>
                  <Collapse eventKey={accordionId} as={AccordionRowContent}>
                    <CodeContent>{text}</CodeContent>
                  </Collapse>
                </Accordion>
              </AccordionRow>
            );
          })}
          {!currentConfigIds ? null : (
            <AccordionRow>
              <Accordion
                activeKey={
                  isCurrentConfigIdsActive ? currentConfigIdsAccordionId : null
                }
                as={AccordionSectionRow}
              >
                <Toggle
                  eventKey={currentConfigIdsAccordionId}
                  as={AccordionRowTitle}
                  isActive={isCurrentConfigIdsActive}
                  onClick={() => {
                    this.setState({
                      remoteConfigAccordion: {
                        ...stateRemoteConfigAccordion,
                        [currentConfigIdsAccordionId]:
                          !isCurrentConfigIdsActive,
                      },
                    });
                  }}
                >
                  Current Config Ids (Group: {currentConfigKey})
                  <AccordionDropDownIcon
                    src={DropDownIcon}
                    isActive={isCurrentConfigIdsActive}
                  />
                </Toggle>
                <Collapse
                  eventKey={currentConfigIdsAccordionId}
                  as={AccordionRowContent}
                >
                  <CodeContent>
                    {JSON.stringify(currentConfigIds, null, 2)}
                  </CodeContent>
                </Collapse>
              </Accordion>
            </AccordionRow>
          )}
        </SectionBody>
      </Section>
    );
  };
  renderConfigurationData = () => {
    const { configurationData } = this.props;
    const {
      shouldShowExtraInformation,
      remoteConfigAccordion: stateRemoteConfigAccordion,
    } = this.state;
    if (!shouldShowExtraInformation || !configurationData) {
      return null;
    }
    return (
      <Section>
        <SectionTitle>Configuration Data</SectionTitle>
        <SectionBody>
          {Object.entries(configurationData).map(([key, value]) => {
            const text = JSON.stringify(value, null, 2);
            const accordionId = `configuration-data-${key}`;
            const isActive = !!stateRemoteConfigAccordion[accordionId];
            return (
              <AccordionRow key={key}>
                <Accordion
                  activeKey={isActive ? accordionId : null}
                  key={accordionId}
                  as={AccordionSectionRow}
                >
                  <Toggle
                    eventKey={accordionId}
                    as={AccordionRowTitle}
                    isActive={isActive}
                    onClick={() => {
                      this.setState({
                        remoteConfigAccordion: {
                          ...stateRemoteConfigAccordion,
                          [accordionId]: !isActive,
                        },
                      });
                    }}
                  >
                    {key}
                    <AccordionDropDownIcon
                      src={DropDownIcon}
                      isActive={isActive}
                    />
                  </Toggle>
                  <Collapse eventKey={accordionId} as={AccordionRowContent}>
                    <CodeContent>{text}</CodeContent>
                  </Collapse>
                </Accordion>
              </AccordionRow>
            );
          })}
        </SectionBody>
      </Section>
    );
  };
  renderRemoteConfig = () => {
    const { remoteConfig } = this.props;
    const { shouldShowExtraInformation, accordionId: stateAccordionId } =
      this.state;
    if (!shouldShowExtraInformation || !remoteConfig) {
      return null;
    }
    const text = JSON.stringify(remoteConfig?.toJS(), null, 2);
    const accordionId = 'remote-config';
    const isActive = stateAccordionId === accordionId;
    return (
      <Section>
        <SectionTitle>Remote Config</SectionTitle>
        <SectionBody>
          <AccordionRow>
            <Accordion activeKey={stateAccordionId} as={AccordionSectionRow}>
              <Toggle
                eventKey={accordionId}
                as={AccordionRowTitle}
                isActive={isActive}
                onClick={(id, event) => {
                  event.stopPropagation();
                  this.setState({
                    accordionId: id,
                  });
                }}
              >
                Remote Config
                <AccordionDropDownIcon src={DropDownIcon} isActive={isActive} />
              </Toggle>
              <Collapse eventKey={accordionId} as={AccordionRowContent}>
                <CodeContent>{text}</CodeContent>
              </Collapse>
            </Accordion>
          </AccordionRow>
        </SectionBody>
      </Section>
    );
  };
  render() {
    const { t, goBack, isSpeedtesting } = this.props;
    return (
      <StyledDebugPanel>
        <Header>
          <BackButton onClick={goBack}>
            <img src={BackIconSource} />
          </BackButton>
          <Title
            onClick={() => {
              this.titleClickTimes += 1;
              if (this.titleClickTimes >= 10) {
                this.setState({
                  shouldShowExtraInformation: true,
                });
              }
            }}
          >
            Debug
          </Title>
          <ActionButton
            onClick={() => this.handleSpeedtestButtonOnClick()}
            disabled={isSpeedtesting}
          >
            {t('general_check', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </ActionButton>
        </Header>
        <Body>
          <UaDescription>{ua}</UaDescription>
          {this.renderSpeedtestPanel()}
          {this.renderDrmPanel()}
          {this.renderPusherPanel()}
          {this.renderIdInformation()}
          {this.renderNtp()}
          {this.renderUserAgentInformation()}
          {this.renderAbPanel()}
          {this.renderFcmTokenPanel()}
          {this.renderConfigurationGroups()}
          {this.renderConfigurationData()}
          {this.renderRemoteConfig()}
        </Body>
      </StyledDebugPanel>
    );
  }
}

DebugPanel.propTypes = {
  t: PropTypes.func.isRequired,
  goBack: PropTypes.func,
  checkNetworkSpeed: PropTypes.func,
  isSpeedtesting: PropTypes.bool,
  isSpeedtested: PropTypes.bool,
  dlProgress: PropTypes.number,
  ulProgress: PropTypes.number,
  pingStatus: PropTypes.string,
  jitterStatus: PropTypes.string,
  dlStatus: PropTypes.string,
  ulStatus: PropTypes.string,
  speedtestUnixTimestamp: PropTypes.number,
  recheckDrmCapability: PropTypes.func,
  drmTestAtUnixTimestamp: PropTypes.number,
  drmTestStatus: PropTypes.string,
  pusherStatsInitSpeed: PropTypes.string,
  pusherStatsConnectSpeed: PropTypes.string,
  trackSpeedtestEvent: PropTypes.func,
  messageId: PropTypes.string,
  abData: PropTypes.object,
  notificationPermission: PropTypes.string,
  fcmToken: PropTypes.string,
  clientTimestamp: PropTypes.number,
  serverTimestamp: PropTypes.number,
  clientId: PropTypes.string,
  sessionId: PropTypes.string,
  configurationData: PropTypes.object,
  configurationGroups: PropTypes.object,
  remoteConfigMerged: PropTypes.object,
  currentConfigIds: PropTypes.array,
  currentConfigKey: PropTypes.string,
  remoteConfig: PropTypes.object,
};

DebugPanel.defaultProps = {
  goBack: () => null,
  checkNetworkSpeed: () => null,
  isSpeedtesting: false,
  isSpeedtested: false,
  dlProgress: null,
  ulProgress: null,
  pingStatus: null,
  jitterStatus: null,
  dlStatus: null,
  ulStatus: null,
  speedtestUnixTimestamp: null,
  recheckDrmCapability: () => null,
  drmTestAtUnixTimestamp: null,
  drmTestStatus: null,
  pusherStatsInitSpeed: null,
  pusherStatsConnectSpeed: null,
  trackSpeedtestEvent: null,
  messageId: null,
  abData: {},
  notificationPermission: '',
  fcmToken: '',
  clientTimestamp: null,
  serverTimestamp: null,
  clientId: null,
  sessionId: null,
  configurationData: null,
  configurationGroups: null,
  remoteConfigMerged: null,
  currentConfigIds: null,
  currentConfigKey: null,
  remoteConfig: null,
};

const StyledDebugPanel = styled(StyledModal)``;

const Body = styled(SettingModalBody)`
  padding-top: 16px;
  padding-bottom: 100px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const UaDescription = styled.div`
  padding: 16px;
  font-size: 14px;
  line-height: 20px;
  white-space: pre-wrap;
  color: ${color.white};
  background-color: ${color.grey['900']};
`;

const TokenRow = styled(Row)`
  height: 100%;
  padding: 10px 0;
`;

const TokenRowTitle = styled(RowTitle)`
  flex-shrink: 0;
`;

const TokenRowContent = styled(RowContent)`
  flex-shrink: 1;
  word-break: break-all;
`;

const CodeContent = styled.div`
  margin-bottom: 16px;
  white-space: pre-wrap;
  line-height: 1.5;
`;

export default withTranslation()(DebugPanel);
